import { Bot, ChevronRight, User } from 'lucide-react'
import { Avatar } from '@/components/design-system/Avatar'
import { auditLogBadgeClass, auditLogLabel } from '@/frontend/lib/auditLogs'

interface AuditLogRow {
  id: string
  audit_action: string
  description: string
  details: Record<string, unknown>
  created_at: string
  actor_name: string | null
  worker_name: string | null
  worker_avatar_url: string | null
}

interface Pagination {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

interface Props {
  audit_logs: AuditLogRow[]
  pagination: Pagination
  onPageChange: (page: number) => void
  emptyMessage?: string
}

export default function AuditLogList({ audit_logs, pagination, onPageChange, emptyMessage }: Props) {
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {audit_logs.length === 0 ? (
        // Estado vazio do DS: uma frase só, média.
        <p className="px-6 py-8 text-[13px] text-muted-foreground">
          {emptyMessage ?? 'Nenhum evento de auditoria encontrado.'}
        </p>
      ) : (
        <div className="divide-y divide-border">
          {audit_logs.map((log) => (
            <details key={log.id} className="group">
              <summary className="list-none cursor-pointer px-4 py-3 hover:bg-accent/50 transition-colors">
                <div className="flex items-center gap-3">
                  <ChevronRight className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-90" />

                  <div className="flex flex-1 flex-col gap-2 md:flex-row md:items-center md:justify-between min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      {/* Slug da ação em mono 10.5, chip neutro — cor é só para status. */}
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-mono text-[10.5px] shrink-0 ${auditLogBadgeClass(log.audit_action)}`}>
                        {auditLogLabel(log.audit_action)}
                      </span>
                      <p className="text-[13px] text-foreground truncate">{log.description}</p>
                    </div>

                    <div className="flex items-center gap-3 shrink-0 text-xs text-muted-foreground">
                      {log.worker_name && (
                        <span className="flex items-center gap-1.5">
                          <Avatar src={log.worker_avatar_url ?? undefined} name={log.worker_name} size="xs" />
                          {log.worker_name}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        {log.actor_name && log.actor_name !== 'system' ? <User className="size-3" /> : <Bot className="size-3" />}
                        {log.actor_name && log.actor_name !== 'system' ? log.actor_name : 'Sistema'}
                      </span>
                      <span className="font-mono text-[11px] text-faint-foreground">{formatDate(log.created_at)}</span>
                    </div>
                  </div>
                </div>
              </summary>

              <div className="ml-7 mr-4 mb-3 rounded-lg border border-border bg-muted/50 overflow-hidden">
                <div className="px-3 py-1.5 border-b border-border bg-muted">
                  <span className="text-xs font-medium text-muted-foreground">Detalhes</span>
                </div>
                <pre className="text-xs p-3 overflow-auto whitespace-pre-wrap text-foreground/80">
                  {JSON.stringify(log.details, null, 2)}
                </pre>
              </div>
            </details>
          ))}
        </div>
      )}

      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-muted">
          <div className="text-[13px] tabular-nums text-muted-foreground">
            Página {pagination.current_page} de {pagination.total_pages} ({pagination.total_count} eventos)
          </div>
          <div className="flex gap-2">
            {pagination.current_page > 1 && (
              <button
                type="button"
                aria-label="Página anterior"
                onClick={() => onPageChange(pagination.current_page - 1)}
                className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-background"
              >
                Anterior
              </button>
            )}
            {pagination.current_page < pagination.total_pages && (
              <button
                type="button"
                aria-label="Próxima página"
                onClick={() => onPageChange(pagination.current_page + 1)}
                className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-background"
              >
                Próxima
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
